/**
 * Patch listing — what the client posts to `PATCH /api/listings/[id]`.
 *
 * PATCH semantics: every field is optional. Absent keys are untouched on
 * the row; provided keys are normalised by the route and applied. The
 * schema is the wire shape, NOT a business-invariant gate — route still
 * enforces:
 *   - ownership (`user_id === auth.uid`)
 *   - auction edit-lock (first bid placed → 403 AUCTION_LOCKED)
 *   - preorder edit-lock (paid orders present → whitelisted keys only)
 *   - commerce/preorder coherence (`applyCommercePatchFields`)
 *   - preorder status transitions (`canAdvanceStatus`)
 *   - taxonomy id resolution + custom-text trimming
 *
 * Wire-truthful 2-variant union, discriminated by `cardCategory`:
 *  - `cardCategory: "trading_card"` → card listing branch; `cardDraft`
 *    payload upserts `listing_card_details` + grading / centering /
 *    defects child rows. The schema permits omitting `cardDraft` (some
 *    edits touch only common/commerce fields).
 *  - `cardCategory` absent OR `null` → lego/regular listing; taxonomy
 *    fields (`brand_id`, `theme_id`, etc.) accepted on this branch only.
 *    Note: legacy producer (`edit-listing-dialog.tsx:502`) sends
 *    `cardCategory: formData.listingCategory` which may be `null` — the
 *    Lego branch accepts both `null` and absent.
 *
 * Two producer shapes today (closing G23 from FRONTEND_WIRING_STRATEGY):
 *  - Main save (`edit-listing-dialog.tsx:596`) — ~25 fields across
 *    common/commerce/taxonomy/preorder/card.
 *  - Preorder-locked path (`edit-listing-dialog.tsx:442`) — 4 fields
 *    only (`shipping_config`, `quantity`, `max_orders`, `min_orders?`);
 *    schema accepts as a degenerate subset of the union.
 *
 * `shipping_config` accepts `unknown[]` (mirrors create schema): the
 * route's `normalizeShippingConfig` accepts both legacy stringified and
 * array forms. Producers always emit an array; tightening would reject
 * pre-mobile-lock clients shipping the legacy shape from cached state.
 *
 * `cardDraft` payload is intentionally permissive (`Record<string,
 * unknown>`): the route reads ~25 optional fields off it. Same reasoning
 * as `listing-create.ts` — tightening over-constrains, route is the
 * strict gate.
 *
 * @see app/api/listings/[id]/route.ts — PATCH handler (boundary safeParse
 *      lands with this contract)
 * @see lib/listings/update-listing.ts — canonical web builder
 * @see components/my-listings/edit-listing-dialog.tsx — sole producer today
 * @see packages/contract/src/listing-create.ts — sibling schema (CREATE)
 * @see .tondocs/todos/listings/p1-edit-listing-l1-l2-l4-hardening.md
 */

import { z } from "zod";
import { CARRIER_PROVIDERS, type CarrierProvider } from "./carrier";
import { ISO_4217_CODES, type Iso4217 } from "./money";
import {
  MIN_PARCEL_HEIGHT_MM,
  MIN_PARCEL_LENGTH_MM,
  MIN_PARCEL_WEIGHT_GRAMS,
  MIN_PARCEL_WIDTH_MM,
} from "./parcel-min-dims";
import { ComicDraftSchema, type ComicDraft } from "./listing-comic";
import { pinned } from "./pin";
import { rawImageRefField, type RawImageRef } from "./raw-image-ref";

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

/**
 * Listing-level fields shared by both card and lego variants. All optional —
 * PATCH semantics. The preorder-locked degenerate path (`shipping_config`,
 * `quantity` only) is just a small subset of this.
 */
interface CommonPatchFields {
  title?: string;
  description?: string | null;
  quantity?: number;
  shipping_config?: unknown[];
  condition?: string;
  grade?: string;
  /**
   * PERSISTENCE array — lands verbatim in `listings.image_urls`, which holds
   * bucket-relative paths since phase C. `RawImageRef` so a display-resolved
   * absolute URL cannot be posted here by an "innocent" resolve upstream.
   * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §9
   */
  image_urls?: RawImageRef[];
  scheduled_publish_at?: string | null;
  /**
   * Shippo white-label (WS3.2 — edit-flow parity for live_rate). Mirrors the
   * 8-key block on `listing-create.ts::CommonListingFields`. The route
   * enforces the same `(shipping_mode, carrier_provider, parcel_*)` CHECK
   * coherence the listings DB does, plus an additional WS3.2.b "locked rate"
   * guard (`LISTING_HAS_INFLIGHT_RATE`): if any `seller_orders` row
   * referencing this listing has a non-terminal `label_status` + an
   * `external_rate_id`, flipping `shipping_mode` is refused.
   */
  shipping_mode?: "legacy" | "live_rate";
  carrier_provider?: CarrierProvider | null;
  /**
   * Canonical parcel dimensions (locked 2026-06-22, policy C in PLAN §A
   * "Parcel units"): mass in grams, distances in millimetres. DB columns
   * renamed to `_grams` / `_mm` in K.6 bundled migration (2026-06-23).
   *
   * Slice C2 (2026-06-23) — the legacy `parcel_weight` / `parcel_length` /
   * `parcel_width` / `parcel_height` + `parcel_mass_unit` /
   * `parcel_distance_unit` transitional fields are GONE. UI form converts
   * locale → canonical before POST.
   */
  parcel_weight_grams?: number | null;
  parcel_length_mm?: number | null;
  parcel_width_mm?: number | null;
  parcel_height_mm?: number | null;
  /**
   * Per-listing ship-from override (ADR_LISTING_SHIP_FROM_ADDRESS.md
   * Phase 1). UUID of an `address_book` row → set the column;
   * `null` → clear the column; absent (key omitted) → leave unchanged.
   * Additive optional — pre-ADR producers stay green.
   */
  ship_from_address_id?: string | null;
  /**
   * ISO 4217 currency code. Accepted on the wire but IGNORED by the
   * server: the listings BEFORE trigger overwrites `currency` from
   * `profiles.currency` (ADR_CURRENCY_PIN — pivoted 2026-06-23). Kept
   * in the PATCH contract so legacy clients sending it don't 400.
   */
  currency?: string;
}

/**
 * Commerce fields. Locked on auctions (first bid placed → 403) and on
 * preorders (route returns the listing unchanged with `PREORDER_LOCKED`
 * for any commerce key). Accepted on the wire — route domain rejects.
 *
 * Phase 3 (2026-06-29) of `shippo-listing-discriminator` collapsed the
 * legacy `accepts_offers` boolean into `listing_format`. Patch consumers
 * that previously sent `accepts_offers: true` now PATCH
 * `listing_format: "fixed_price_with_offers"` directly; the bool
 * is no longer accepted on the wire.
 */
interface CommercePatchFields {
  listing_format?:
    | "fixed_price"
    | "fixed_price_with_offers"
    | "offers_only"
    | "auction"
    | null;
  auto_decline_below?: number | null;
  price?: number | null;
}

/**
 * Taxonomy fields — lego variant only. Card listings resolve their
 * taxonomy via `cardDraft.tcgId` / `cardDraft.tcgSetId` / etc., NOT via
 * these top-level ids (mirrors `listing-create.ts`).
 */
interface TaxonomyPatchFields {
  brand_id?: string | null;
  theme_id?: string | null;
  product_type_id?: string | null;
  brand_custom_text?: string | null;
  theme_custom_text?: string | null;
  product_type_custom_text?: string | null;
}

/**
 * Preorder fields. `preorder_status` drives the transition FSM
 * (`canAdvanceStatus`); other fields are the open-only patch surface.
 * Producer (`edit-listing-dialog.tsx:584-587`) only sends the non-status
 * fields when the preorder is unlocked; `preorder_status` is sent by a
 * separate status-transition button (lives outside this dialog today).
 */
interface PreorderPatchFields {
  preorder_status?: "open" | "funded" | "in_production" | "shipped";
  preorder_deadline?: string | null;
  estimated_delivery?: string | null;
  min_orders?: number | null;
  max_orders?: number | null;
}

export interface PatchListingRequestCard
  extends CommonPatchFields,
    CommercePatchFields,
    PreorderPatchFields {
  cardCategory: "trading_card";
  /**
   * Card draft — see `listing-create.ts` for the field menu the route
   * reads (`tcgId`/`tcgSetId`/`grading.*`/`centering.*`/etc.).
   * Optional on PATCH — many card edits touch only price/quantity.
   */
  cardDraft?: Record<string, unknown>;
  /** Absent on the card branch — mirror so union access is type-safe. */
  comicDraft?: undefined;
}

export interface PatchListingRequestComic
  extends CommonPatchFields,
    CommercePatchFields,
    PreorderPatchFields {
  cardCategory: "comic";
  /** Typed draft (ComicDraftSchema) — optional, many edits touch only price. */
  comicDraft?: ComicDraft;
  /** Absent on the comic branch — mirror so union access is type-safe. */
  cardDraft?: undefined;
}

export interface PatchListingRequestLego
  extends CommonPatchFields,
    CommercePatchFields,
    TaxonomyPatchFields,
    PreorderPatchFields {
  /**
   * `"lego"`, `null`, or absent — the discriminator. Producer
   * (`edit-listing-dialog.tsx:{502,575}`) sends `cardCategory:
   * formData.listingCategory`, where `listingCategory` is typed
   * `"lego" | "trading_card" | undefined` (`types/listing.ts:3` +
   * `components/feed/feed-filters.tsx:33` extends with `null`).
   * `undefined` is dropped by JSON.stringify, `"lego"` and `null`
   * both ride the wire — both land here, both mean lego/regular.
   */
  cardCategory?: "lego" | null;
  /**
   * Accepted but ignored on the lego branch — producer always emits
   * `cardDraft: formData.cardDraft` regardless of `listingCategory`
   * (`edit-listing-dialog.tsx:503,576`). Stripping it from the lego
   * shape would reject every existing edit save.
   */
  cardDraft?: Record<string, unknown>;
  /** Absent on the lego branch — mirror so union access is type-safe. */
  comicDraft?: undefined;
}

export type PatchListingRequest =
  | PatchListingRequestCard
  | PatchListingRequestComic
  | PatchListingRequestLego;

// ---------------------------------------------------------------------------
// Zod shapes
// ---------------------------------------------------------------------------

const CommonShape = {
  title: z.string().optional(),
  description: z.string().nullish(),
  quantity: z.number().optional(),
  shipping_config: z.array(z.unknown()).optional(),
  condition: z.string().optional(),
  grade: z.string().optional(),
  image_urls: z.array(rawImageRefField()).optional(),
  scheduled_publish_at: z.string().nullish(),
  // Shippo white-label (WS3.2) — mirror of `listing-create.ts`.
  shipping_mode: z.enum(["legacy", "live_rate"]).optional(),
  carrier_provider: z.enum(CARRIER_PROVIDERS).nullish(),
  // Canonical (locked 2026-06-22, K.6) — server writes these. Slice C2
  // (2026-06-23) dropped the legacy locale-shape fields; UI form converts.
  //
  // Floor pin (Option A, 2026-06-30): tightened from `.positive()` to
  // `.min(MIN_*)` so a publish/edit cannot save sub-carrier-minimum dims.
  // Constants live in `@kraahl/contract/parcel-min-dims` — same SoT as the
  // sibling `listing-create.ts` schema and the rate-quote-time domain gate.
  // @see .tondocs/shippo/PARCEL_MIN_DIMS.md §5 Option A
  parcel_weight_grams: z.number().min(MIN_PARCEL_WEIGHT_GRAMS).nullish(),
  parcel_length_mm: z.number().min(MIN_PARCEL_LENGTH_MM).nullish(),
  parcel_width_mm: z.number().min(MIN_PARCEL_WIDTH_MM).nullish(),
  parcel_height_mm: z.number().min(MIN_PARCEL_HEIGHT_MM).nullish(),
  // Per-listing ship-from override — additive optional, see CommonPatchFields.
  ship_from_address_id: z.string().uuid().nullish(),
  // Currency — accepted but ignored. Server trigger overwrites from
  // `profiles.currency`. See CommonPatchFields.currency comment + ADR.
  currency: z.string().optional(),
} as const;

/**
 * Cross-field coherence: when the producer sets `shipping_mode='live_rate'`
 * in the PATCH body, every parcel + carrier field must be present (PATCH
 * semantics: absent fields are untouched server-side, but a deliberate
 * `shipping_mode='live_rate'` write demands the full block). Mirrors the
 * sibling refinement on `listing-create.ts`. `LIVE_RATE_PARCEL_REQUIRED`
 * params key kept identical for FE error-mapping.
 */
function refineLiveRateCoherence(
  body: {
    shipping_mode?: "legacy" | "live_rate";
    carrier_provider?: CarrierProvider | null;
    parcel_weight_grams?: number | null;
    parcel_length_mm?: number | null;
    parcel_width_mm?: number | null;
    parcel_height_mm?: number | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (body.shipping_mode !== "live_rate") return;
  if (body.carrier_provider == null) {
    ctx.addIssue({
      code: "custom",
      path: ["carrier_provider"],
      message: "carrier_provider is required when shipping_mode='live_rate'",
      params: { code: "LIVE_RATE_PARCEL_REQUIRED" },
    });
  }
  // Canonical-only (slice C2): all 4 _grams / _mm fields must be present.
  const canonicalComplete =
    body.parcel_weight_grams != null &&
    body.parcel_length_mm != null &&
    body.parcel_width_mm != null &&
    body.parcel_height_mm != null;
  if (!canonicalComplete) {
    ctx.addIssue({
      code: "custom",
      path: ["parcel_weight_grams"],
      message:
        "parcel dimensions are required when shipping_mode='live_rate' " +
        "(parcel_weight_grams + parcel_length_mm + parcel_width_mm + parcel_height_mm)",
      params: { code: "LIVE_RATE_PARCEL_REQUIRED" },
    });
  }
}

const CommerceShape = {
  listing_format: z
    .enum(["fixed_price", "fixed_price_with_offers", "offers_only", "auction"])
    .nullish(),
  auto_decline_below: z.number().nullish(),
  price: z.number().nullish(),
} as const;

const TaxonomyShape = {
  brand_id: z.string().nullish(),
  theme_id: z.string().nullish(),
  product_type_id: z.string().nullish(),
  brand_custom_text: z.string().nullish(),
  theme_custom_text: z.string().nullish(),
  product_type_custom_text: z.string().nullish(),
} as const;

const PreorderShape = {
  preorder_status: z
    .enum(["open", "funded", "in_production", "shipped"])
    .optional(),
  preorder_deadline: z.string().nullish(),
  estimated_delivery: z.string().nullish(),
  min_orders: z.number().nullish(),
  max_orders: z.number().nullish(),
} as const;

const CardPatchSchema = z
  .object({
    cardCategory: z.literal("trading_card"),
    cardDraft: z.record(z.string(), z.unknown()).optional(),
    comicDraft: z.undefined().optional(),
    ...CommonShape,
    ...CommerceShape,
    ...PreorderShape,
  })
  .superRefine(refineLiveRateCoherence);

const ComicPatchSchema = z
  .object({
    cardCategory: z.literal("comic"),
    comicDraft: ComicDraftSchema.optional(),
    cardDraft: z.undefined().optional(),
    ...CommonShape,
    ...CommerceShape,
    ...PreorderShape,
  })
  .superRefine(refineLiveRateCoherence);

const LegoPatchSchema = z
  .object({
    // `"lego" | null | undefined` — JSON.stringify keeps `"lego"`/`null`,
    // drops `undefined`. See header for producer-source justification.
    cardCategory: z.union([z.literal("lego"), z.null()]).optional(),
    cardDraft: z.record(z.string(), z.unknown()).optional(),
    comicDraft: z.undefined().optional(),
    ...CommonShape,
    ...CommerceShape,
    ...TaxonomyShape,
    ...PreorderShape,
  })
  .superRefine(refineLiveRateCoherence);

// Lego (catch-all discriminator values) stays LAST.
export const PatchListingRequestSchema = pinned<PatchListingRequest>()(
  z.union([CardPatchSchema, ComicPatchSchema, LegoPatchSchema]),
);

// ---------------------------------------------------------------------------
// Response — F-04 (2026-06-24)
// ---------------------------------------------------------------------------

/**
 * Response shape for `PATCH /api/listings/[id]`.
 *
 * `currency` echo is REQUIRED for the same reason as
 * {@link CreateListingResponse} — the K.6 BEFORE-trigger pins
 * `listings.currency := profiles.currency` silently on UPDATE as well as
 * INSERT. The `listing` row passes through unchanged for back-compat with
 * existing card-detail UPSERT consumers; the top-level `currency` field is
 * the new echo gate.
 *
 * @see .tondocs/payments/ADR_CURRENCY_PIN.md
 * @see app/api/listings/[id]/route.ts (all 4 success returns)
 */
export interface PatchListingResponse {
  success: true;
  listing: unknown;
  currency: Iso4217;
}

export const PatchListingResponseSchema = pinned<PatchListingResponse>()(
  z.object({
    success: z.literal(true),
    listing: z.unknown(),
    currency: z.enum(ISO_4217_CODES),
  }),
);
